import type { GameEvent } from '../events/gameEvents';
import type { SeasonState } from '../seasons/SeasonTypes';
import type { ResourceState, WorldEntity } from '../world/types';
import { BrowserGameplayStorage, type GameplayStorage } from './GameplayStorage';
import type {
  CitizenState,
  CommandLogEntry,
  CommunityProject,
  CommunityState,
  CommunityVote,
  DevelopmentPhase,
  DiplomacyState,
  ExtendedResourceName,
  ExtendedResourceState,
  GameplaySnapshot,
  JobType,
  MajorEvent,
  OfflineProgressSummary,
  ProductionChain,
  QuestState,
  ResearchProject,
  ViewerCharacter,
} from './GameplayTypes';

interface StoredGameplayState {
  readonly viewerCharacters: readonly ViewerCharacter[];
  readonly resources: ExtendedResourceState;
  readonly projects: readonly CommunityProject[];
  readonly votes: readonly CommunityVote[];
  readonly commandLog: readonly CommandLogEntry[];
}

interface CommandResult {
  readonly title: string;
  readonly cameraHint?: 'join' | 'level_up' | 'project' | 'vote' | 'quest';
}

const phases: readonly DevelopmentPhase[] = [
  'Nomadenlager',
  'Dorf',
  'Großdorf',
  'Kleinstadt',
  'Stadt',
  'Großstadt',
  'Festung',
  'Königreich',
  'Imperium',
];

const resourceNames: readonly ExtendedResourceName[] = [
  'wood',
  'stone',
  'food',
  'gold',
  'water',
  'clay',
  'tools',
  'iron',
  'coal',
  'wool',
  'cloth',
  'bread',
  'fish',
  'meat',
  'beer',
  'planks',
  'flour',
  'grain',
  'steel',
  'glass',
  'paper',
  'spices',
  'gems',
];

const jobByKind: Record<string, JobType> = {
  worker: 'baumeister',
  woodcutter: 'holzfaeller',
  trader: 'haendler',
  knight: 'soldat',
  farmer: 'bauer',
  miner: 'bergmann',
};

export class GameplayManager {
  private readonly storage: GameplayStorage<StoredGameplayState>;
  private resources: ExtendedResourceState;
  private readonly citizens: CitizenState[];
  private readonly viewerCharacters: ViewerCharacter[];
  private readonly productionChains: ProductionChain[];
  private readonly research: ResearchProject[];
  private readonly quests: QuestState[];
  private readonly projects: CommunityProject[];
  private readonly votes: CommunityVote[];
  private readonly majorEvents: MajorEvent[];
  private readonly commandLog: CommandLogEntry[];
  private readonly unlockedResearch = new Set<string>();
  private readonly diplomacy: DiplomacyState = createDiplomacyState();
  private elapsedMs = 0;
  private phaseIndex = 1;

  constructor(
    initialResources: ResourceState,
    initialUnits: readonly WorldEntity[],
    storage: GameplayStorage<StoredGameplayState> = new BrowserGameplayStorage('stream-kingdoms-gameplay-v2'),
  ) {
    this.storage = storage;
    const stored = this.storage.load();
    this.resources = stored?.resources ? cloneResources(stored.resources) : createExtendedResources(initialResources);
    this.viewerCharacters = stored?.viewerCharacters ? stored.viewerCharacters.map(cloneViewerCharacter) : [];
    this.projects = stored?.projects ? stored.projects.map(cloneProject) : createCommunityProjects();
    this.votes = stored?.votes ? stored.votes.map(cloneVote) : createCommunityVotes();
    this.commandLog = stored?.commandLog ? stored.commandLog.slice(0, 20).map((entry) => ({ ...entry })) : [];
    this.citizens = initialUnits.map((unit, index) => createCitizenFromUnit(unit, index));
    this.productionChains = createProductionChains();
    this.research = createResearchProjects();
    this.quests = createQuests();
    this.majorEvents = createMajorEvents();

    for (const character of this.viewerCharacters) {
      if (!this.citizens.some((citizen) => citizen.id === `viewer-${character.twitchId}`)) {
        this.citizens.push(createCitizenFromViewer(character));
      }
    }
  }

  update(deltaMs: number, season: SeasonState, buildings: readonly WorldEntity[], progressionLevel: number): void {
    this.elapsedMs += deltaMs;
    this.phaseIndex = Math.max(this.phaseIndex, calculatePhaseIndex(this.viewerCharacters.length, this.citizens.length, buildings.length, progressionLevel));
    this.updateCitizens(season, buildings);
    this.updateProductionChains(deltaMs, season);
    this.updateResearch(deltaMs);
    this.updateQuests();
    this.updateVotes();
    this.persist();
  }

  applyEvent(event: GameEvent, season: SeasonState): CommandResult | undefined {
    if (event.type === 'viewer_joined') return this.joinViewer(event.viewerName, Date.now(), season);

    if (event.type === 'resource_collected') {
      this.resources[event.resource] += Math.round(event.amount * season.production[event.resource]);
      this.addCharacterProgress(event.viewerName, 16, 2);
      this.addQuestProgress('community_resources', event.amount);
      this.logCommand('!work', event.viewerName, `${event.viewerName} sammelt für das Reich`, true);
      return { title: `${event.viewerName} stärkt die Vorratskammer` };
    }

    if (event.type === 'building_completed') {
      this.addQuestProgress('first_district', 1);
      this.resources.tools = Math.max(0, this.resources.tools - 8);
      this.resources.planks = Math.max(0, this.resources.planks - 16);
      return { title: 'Ein Bauprojekt wurde abgeschlossen', cameraHint: 'project' };
    }

    if (event.type === 'village_level_up') {
      this.phaseIndex = Math.max(this.phaseIndex, Math.min(phases.length - 1, event.level - 1));
      return { title: `Das Reich erreicht ${phases[this.phaseIndex]}`, cameraHint: 'level_up' };
    }

    return undefined;
  }

  handleCommand(command: string, viewerName: string, now = Date.now(), season?: SeasonState): CommandResult {
    const normalized = command.trim().toLowerCase();
    if (normalized === '!join') return this.joinViewer(viewerName, now, season);
    if (normalized === '!me' || normalized === '!level') return this.describeViewer(viewerName, normalized);
    if (normalized === '!work') return this.viewerWork(viewerName, season);
    if (normalized.startsWith('!vote')) return this.castVote(viewerName, normalized);
    if (normalized.startsWith('!project')) return this.contributeProject(viewerName);
    if (normalized === '!inventory') return this.describeInventory(viewerName);
    if (normalized === '!job') return this.describeJob(viewerName);

    this.logCommand(normalized, viewerName, 'Unbekannter Befehl', false);
    return { title: `${viewerName}: unbekannter Befehl` };
  }

  getSnapshot(): GameplaySnapshot {
    const chatterCount = this.citizens.filter((citizen) => citizen.origin === 'chatter').length;
    const npcCount = this.citizens.length - chatterCount;
    const score = calculateProgressionScore(this.viewerCharacters.length, this.citizens.length, this.citizens.length, this.phaseIndex);
    const community: CommunityState = {
      resources: { ...this.resources },
      projects: this.projects.map(cloneProject),
      votes: this.votes.map(cloneVote),
      researchQueue: this.research.filter((project) => !project.completed).map((project) => ({ ...project })),
      diplomacy: this.diplomacy,
      kingdomProgression: {
        phase: phases[this.phaseIndex],
        phaseIndex: this.phaseIndex,
        score,
      },
      majorEvents: this.majorEvents.map((event) => ({ ...event })),
    };

    return {
      phase: phases[this.phaseIndex],
      phaseIndex: this.phaseIndex,
      population: this.citizens.length,
      chatterCount,
      npcCount,
      resources: { ...this.resources },
      citizens: this.citizens.map((citizen) => ({
        ...citizen,
        inventory: { ...citizen.inventory },
      })),
      viewerCharacters: this.viewerCharacters.map(cloneViewerCharacter),
      community,
      productionChains: this.productionChains.map((chain) => ({ ...chain })),
      activeResearch: this.research.filter((project) => !project.completed).map((project) => ({ ...project })),
      activeQuests: this.quests.filter((quest) => !quest.completed).map((quest) => ({ ...quest })),
      unlockedResearch: [...this.unlockedResearch],
      balancing: {
        chatterProductivity: 1,
        npcProductivity: 0.5,
        npcLimit: calculateNpcLimit(chatterCount),
      },
      commandLog: this.commandLog.slice(0, 12).map((entry) => ({ ...entry })),
    };
  }

  private joinViewer(viewerName: string, now: number, season?: SeasonState): CommandResult {
    const twitchId = viewerName.toLowerCase();
    const existing = this.viewerCharacters.find((character) => character.twitchId === twitchId);
    if (existing) {
      const offline = calculateOfflineProgress(existing, now, season);
      existing.isOnline = true;
      existing.lastSeenAt = now;
      existing.offlineProgress = offline;
      existing.experience += offline.professionXp;
      existing.reputation += 1;
      existing.mood = Math.min(100, existing.mood + 6);
      addResources(this.resources, offline.resources);
      this.syncCitizenFromViewer(existing);
      this.logCommand('!join', viewerName, offline.message, true);
      return { title: `${viewerName} kehrt zurück: ${offline.message}`, cameraHint: 'join' };
    }

    const profession = chooseStartProfession(this.viewerCharacters.length);
    const character = createViewerCharacter(viewerName, profession, now, findHomeId(), findWorkplaceId(profession));
    this.viewerCharacters.push(character);
    this.citizens.push(createCitizenFromViewer(character));
    this.addQuestProgress('first_population', 1);
    this.logCommand('!join', viewerName, 'Charakter erstellt', true);
    return { title: `${viewerName} tritt dem Reich als ${jobLabel(profession)} bei`, cameraHint: 'join' };
  }

  private describeViewer(viewerName: string, command: string): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) {
      this.logCommand(command, viewerName, 'Noch nicht beigetreten', false);
      return { title: `${viewerName} muss zuerst !join nutzen` };
    }
	const title = `${character.displayName}: Stufe ${character.level}, ${jobLabel(character.profession)} Berufsstufe ${character.professionLevel}, Ruf ${character.reputation}`;
    this.logCommand(command, viewerName, title, true);
    return { title };
  }

  private viewerWork(viewerName: string, season?: SeasonState): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) return { title: `${viewerName} muss zuerst !join nutzen` };
    const output = resourceForProfession(character.profession);
    const amount = Math.round(12 * professionModifier(character.profession, season) * softCap(character.level));
    this.resources[output] += amount;
    character.experience += 18;
    character.professionLevel = Math.max(character.professionLevel, Math.floor(character.experience / 160) + 1);
    character.level = Math.max(character.level, Math.floor(character.experience / 240) + 1);
    character.currentTask = `${jobLabel(character.profession)} arbeitet für ${amount} ${output}`;
    this.addQuestProgress('community_resources', amount);
    this.logCommand('!work', viewerName, `${amount} ${output}`, true);
    return { title: `${viewerName} arbeitet: +${amount} ${resourceLabel(output)}` };
  }

  private castVote(viewerName: string, command: string): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) return { title: `${viewerName} muss zuerst !join nutzen` };
    const vote = this.votes.find((candidate) => !candidate.closed);
    if (!vote) return { title: 'Aktuell läuft keine Abstimmung' };
    const choice = command.replace('!vote', '').trim() || vote.choices[0];
    const acceptedChoice = vote.choices.includes(choice) ? choice : vote.choices[0];
    vote.votes[character.twitchId] = acceptedChoice;
    this.logCommand('!vote', viewerName, `Stimme für ${acceptedChoice}`, true);
    return { title: `${viewerName} stimmt für ${acceptedChoice}`, cameraHint: 'vote' };
  }

  private contributeProject(viewerName: string): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) return { title: `${viewerName} muss zuerst !join nutzen` };
    const project = this.projects.find((candidate) => !candidate.completed);
    if (!project) return { title: 'Kein aktives Großprojekt' };
    const contribution = Math.round(24 * softCap(character.level));
    project.contributions[character.twitchId] = (project.contributions[character.twitchId] ?? 0) + contribution;
    project.progress = Math.min(project.goal, project.progress + contribution);
    project.completed = project.progress >= project.goal;
    character.reputation += 2;
    character.experience += 20;
    this.logCommand('!project', viewerName, `+${contribution} für ${project.label}`, true);
    return { title: `${viewerName} hilft bei ${project.label}: ${Math.round((project.progress / project.goal) * 100)}%`, cameraHint: 'project' };
  }

  private describeInventory(viewerName: string): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) return { title: `${viewerName} muss zuerst !join nutzen` };
    const entries = Object.entries(character.inventory).filter(([, amount]) => Number(amount) > 0);
    const title = entries.length ? `${viewerName} Inventar: ${entries.map(([key, value]) => `${key} ${value}`).join(', ')}` : `${viewerName} hat ein leeres Inventar`;
    this.logCommand('!inventory', viewerName, title, true);
    return { title };
  }

  private describeJob(viewerName: string): CommandResult {
    const character = this.findViewer(viewerName);
    if (!character) return { title: `${viewerName} muss zuerst !join nutzen` };
    const title = `${viewerName} ist ${jobLabel(character.profession)} bei ${character.workplaceId ?? 'Gemeinschaftsarbeit'}`;
    this.logCommand('!job', viewerName, title, true);
    return { title };
  }

  private addCharacterProgress(viewerName: string, xp: number, reputation: number): void {
    const character = this.findViewer(viewerName);
    if (!character) return;
    character.experience += xp;
    character.reputation += reputation;
    character.level = Math.max(character.level, Math.floor(character.experience / 240) + 1);
    character.professionLevel = Math.max(character.professionLevel, Math.floor(character.experience / 160) + 1);
    this.syncCitizenFromViewer(character);
  }

  private updateCitizens(season: SeasonState, buildings: readonly WorldEntity[]): void {
    const daySegment = Math.floor((this.elapsedMs / 10000) % 4);
    for (const citizen of this.citizens) {
      const workplace = findWorkplaceForJob(citizen.job, buildings);
      citizen.hunger = Math.min(100, citizen.hunger + (season.currentSeason === 'winter' ? 0.08 : 0.04));
      citizen.stamina = Math.max(0, Math.min(100, citizen.stamina + (daySegment === 3 ? 0.16 : -0.05)));
      citizen.morale = Math.max(0, Math.min(100, citizen.morale + (season.currentWeather === 'storm' ? -0.04 : 0.02)));
      citizen.currentTask = describeTask(citizen.job, daySegment, workplace?.label ?? workplace?.kind);
    }
  }

  private updateProductionChains(deltaMs: number, season: SeasonState): void {
    for (const chain of this.productionChains) {
      const speed = deltaMs * 0.000018 * season.production.food;
      chain.progress = Math.min(1, chain.progress + speed);
      if (chain.progress < 1) continue;
      chain.progress = 0;
      for (const resource of chain.output) this.resources[resource] += chain.id === 'grain_to_bread' ? 3 : 2;
      chain.active = true;
    }
  }

  private updateResearch(deltaMs: number): void {
    const active = this.research.find((project) => !project.completed);
    if (!active) return;
    const researcherCount = this.citizens.filter((citizen) => citizen.job === 'forscher').length || 1;
    active.progress = Math.min(1, active.progress + deltaMs * 0.000012 * researcherCount);
    if (active.progress >= 1) {
      active.completed = true;
      for (const unlock of active.unlocks) this.unlockedResearch.add(unlock);
    }
  }

  private updateQuests(): void {
    const populationQuest = this.quests.find((quest) => quest.id === 'first_population');
    if (populationQuest && !populationQuest.completed) {
      populationQuest.progress = Math.min(populationQuest.goal, this.viewerCharacters.length);
      populationQuest.completed = populationQuest.progress >= populationQuest.goal;
    }
  }

  private updateVotes(): void {
    const now = Date.now();
    for (const vote of this.votes) {
      if (vote.closed || now < vote.endsAt) continue;
      vote.closed = true;
      vote.winner = chooseVoteWinner(vote);
      this.logCommand('!vote', 'System', `${vote.label}: ${vote.winner ?? 'keine Entscheidung'}`, true);
    }
  }

  private addQuestProgress(id: string, amount: number): void {
    const quest = this.quests.find((candidate) => candidate.id === id);
    if (!quest || quest.completed) return;
    quest.progress = Math.min(quest.goal, quest.progress + amount);
    quest.completed = quest.progress >= quest.goal;
    if (!quest.completed) return;
    addResources(this.resources, quest.reward);
  }

  private findViewer(viewerName: string): ViewerCharacter | undefined {
    return this.viewerCharacters.find((character) => character.twitchName.toLowerCase() === viewerName.toLowerCase());
  }

  private syncCitizenFromViewer(character: ViewerCharacter): void {
    const citizen = this.citizens.find((candidate) => candidate.id === `viewer-${character.twitchId}`);
    if (!citizen) {
      this.citizens.push(createCitizenFromViewer(character));
      return;
    }
    citizen.name = character.displayName;
    citizen.job = character.profession;
    citizen.health = character.health;
    citizen.morale = character.mood;
    citizen.hunger = character.hunger;
    citizen.stamina = character.stamina;
    citizen.xp = character.experience;
    citizen.reputation = character.reputation;
    citizen.currentTask = character.currentTask;
  }

  private logCommand(command: string, viewerName: string, result: string, accepted: boolean): void {
    this.commandLog.unshift({
      id: `cmd-${Date.now()}-${this.commandLog.length}`,
      command,
      viewerName,
      createdAt: Date.now(),
      result,
      accepted,
    });
    this.commandLog.length = Math.min(this.commandLog.length, 30);
  }

  private persist(): void {
    this.storage.save({
      viewerCharacters: this.viewerCharacters.map(cloneViewerCharacter),
      resources: { ...this.resources },
      projects: this.projects.map(cloneProject),
      votes: this.votes.map(cloneVote),
      commandLog: this.commandLog.slice(0, 20).map((entry) => ({ ...entry })),
    });
  }
}

function createExtendedResources(base: ResourceState): ExtendedResourceState {
  const resources = Object.fromEntries(resourceNames.map((resource) => [resource, 0])) as ExtendedResourceState;
  resources.wood = base.wood;
  resources.stone = base.stone;
  resources.food = base.food;
  resources.gold = base.gold;
  resources.water = 400;
  resources.tools = 80;
  resources.iron = 30;
  resources.planks = 120;
  resources.grain = 60;
  return resources;
}

function createViewerCharacter(viewerName: string, profession: JobType, now: number, homeId: string, workplaceId?: string): ViewerCharacter {
  const twitchId = viewerName.toLowerCase();
  return {
    twitchId,
    ownerTwitchUserId: twitchId,
    twitchName: viewerName,
    displayName: viewerName,
    profession,
    homeId,
    workplaceId,
    inventory: { tool: 1, food: 2 },
    level: 1,
    experience: 0,
    professionLevel: 1,
    reputation: 0,
    titles: ['Neuankommling'],
    achievements: [],
    currentTask: 'sucht einen Platz im Reich',
    mood: 75,
    hunger: 15,
    health: 100,
    stamina: 90,
    lastSeenAt: now,
    isOnline: true,
    offlineProgress: createEmptyOfflineProgress(now),
  };
}

function createCitizenFromViewer(character: ViewerCharacter): CitizenState {
  return {
    id: `viewer-${character.twitchId}`,
    name: character.displayName,
    origin: 'chatter',
    job: character.profession,
    age: 21,
    homeId: character.homeId,
    workplaceId: character.workplaceId,
    inventory: {},
    productivity: 1,
    health: character.health,
    morale: character.mood,
    hunger: character.hunger,
    stamina: character.stamina,
    xp: character.experience,
    reputation: character.reputation,
    currentTask: character.currentTask,
  };
}

function createCitizenFromUnit(unit: WorldEntity, index: number): CitizenState {
  return {
    id: `citizen-${unit.id}`,
    name: unit.label ?? `Chatter ${index + 1}`,
    origin: 'chatter',
    job: jobByKind[unit.kind] ?? 'baumeister',
    age: 18 + index * 4,
    homeId: 'community-house',
    workplaceId: unit.kind,
    inventory: {},
    productivity: 1,
    health: 100,
    morale: 82,
    hunger: 12,
    stamina: 92,
    xp: 120 + index * 30,
    reputation: 25,
    currentTask: 'wartet auf Auftrag',
  };
}

function calculateOfflineProgress(character: ViewerCharacter, now: number, season?: SeasonState): OfflineProgressSummary {
  const elapsedHours = Math.max(0, (now - character.lastSeenAt) / 3600000);
  const cappedHours = Math.min(24, elapsedHours);
  const resource = resourceForProfession(character.profession);
  const amount = Math.round(cappedHours * 5 * professionModifier(character.profession, season) * softCap(character.level));
  const professionXp = Math.round(cappedHours * 4);
  return {
    from: character.lastSeenAt,
    to: now,
    cappedHours,
    resources: amount > 0 ? { [resource]: amount } : {},
    professionXp,
    helpedProjectId: cappedHours > 2 ? 'city_wall' : undefined,
    message:
      cappedHours < 0.1
        ? 'war nur kurz weg'
        : `${Math.round(cappedHours)}h offline, +${amount} ${resourceLabel(resource)}, +${professionXp} Berufs-XP`,
  };
}

function createEmptyOfflineProgress(now: number): OfflineProgressSummary {
  return {
    from: now,
    to: now,
    cappedHours: 0,
    resources: {},
    professionXp: 0,
    message: 'kein Offline-Fortschritt',
  };
}

function createProductionChains(): ProductionChain[] {
  return [
    {
      id: 'tree_to_tools',
      label: 'Holz -> Bretter -> Werkzeuge',
      input: ['wood', 'iron'],
      output: ['planks', 'tools'],
      buildings: ['woodcutter_camp', 'smithy', 'warehouse'],
      visibleRoute: ['Wald', 'Holzfällerhütte', 'Schmiede', 'Lagerhaus'],
      progress: 0.35,
      active: true,
    },
    {
      id: 'grain_to_bread',
      label: 'Getreide -> Mehl -> Brot',
      input: ['grain', 'water'],
      output: ['flour', 'bread', 'food'],
      buildings: ['farm', 'mill', 'bakery'],
      visibleRoute: ['Farm', 'Mühle', 'Bäckerei', 'Lagerhaus'],
      progress: 0.62,
      active: true,
    },
  ];
}

function createResearchProjects(): ResearchProject[] {
  return [
    { id: 'roads', label: 'Straßenbau', unlocks: ['stone_roads', 'faster_trade'], progress: 0.28, cost: { stone: 120, tools: 8 }, completed: false },
    { id: 'agriculture', label: 'Landwirtschaft', unlocks: ['irrigation', 'field_yield_bonus'], progress: 0, cost: { food: 240, water: 80 }, completed: false },
  ];
}

function createQuests(): QuestState[] {
  return [
    { id: 'community_resources', label: 'Gemeinschaftsauftrag: 1000 Ressourcen sammeln', target: 'Ressourcen', progress: 0, goal: 1000, completed: false, reward: { gold: 250 } },
    { id: 'first_district', label: 'Dorfkern ausbauen: 3 Gebäude fertigstellen', target: 'Gebäude', progress: 0, goal: 3, completed: false, reward: { tools: 12, wood: 180 } },
    { id: 'first_population', label: '10 Einwohner erreichen', target: 'Einwohner', progress: 0, goal: 10, completed: false, reward: { food: 220, beer: 20 } },
  ];
}

function createCommunityProjects(): CommunityProject[] {
  return [
    {
      id: 'city_wall',
      label: 'Stadtmauer',
      type: 'stadtmauer',
      progress: 180,
      goal: 1000,
      requiredResources: { stone: 800, wood: 220, tools: 40 },
      contributions: {},
      completed: false,
    },
    {
      id: 'harbor',
      label: 'Hafen',
      type: 'hafen',
      progress: 0,
      goal: 1400,
      requiredResources: { wood: 600, stone: 300, tools: 60 },
      contributions: {},
      completed: false,
    },
  ];
}

function createCommunityVotes(): CommunityVote[] {
  const now = Date.now();
  return [
    {
      id: 'next_focus',
      label: 'Nächster Fokus',
      choices: ['Stadtmauer', 'Landwirtschaft', 'Handel'],
      startedAt: now,
      endsAt: now + 90000,
      cooldownUntil: now + 120000,
      votes: {},
      antiSpamWindowMs: 10000,
      closed: false,
    },
  ];
}

function createDiplomacyState(): DiplomacyState {
  return {
    aiKingdoms: [
      { id: 'nordmark', name: 'Nordmark', relation: 'neutral', score: 5, activeOffer: 'Holz gegen Fisch' },
      { id: 'dornfels', name: 'Dornfels', relation: 'angespannt', score: -25 },
      { id: 'silberfeste', name: 'Silberfeste', relation: 'verbundet', score: 72 },
    ],
  };
}

function createMajorEvents(): MajorEvent[] {
  return [{ id: 'spring_festival', label: 'Volksfest vorbereiten', type: 'festival', severity: 1, progress: 0.35, resolved: false }];
}

function chooseStartProfession(index: number): JobType {
  const jobs: readonly JobType[] = ['holzfaeller', 'bauer', 'bergmann', 'haendler', 'baumeister', 'wache'];
  return jobs[index % jobs.length];
}

function findHomeId(): string {
  return 'community-house';
}

function findWorkplaceId(job: JobType): string | undefined {
  const workplaceByJob: Partial<Record<JobType, string>> = {
    holzfaeller: 'woodcutter_camp',
    bauer: 'farm',
    bergmann: 'mine',
    steinmetz: 'quarry',
    schmied: 'smithy',
    baecker: 'bakery',
    mueller: 'mill',
    haendler: 'market',
    soldat: 'barracks',
    wache: 'watchtower',
    baumeister: 'warehouse',
  };
  return workplaceByJob[job];
}

function addResources(resources: ExtendedResourceState, delta: Partial<Record<ExtendedResourceName, number>>): void {
  for (const [key, value] of Object.entries(delta)) resources[key as ExtendedResourceName] += value ?? 0;
}

function resourceForProfession(job: JobType): ExtendedResourceName {
  const map: Partial<Record<JobType, ExtendedResourceName>> = {
    holzfaeller: 'wood',
    bauer: 'grain',
    jaeger: 'meat',
    fischer: 'fish',
    bergmann: 'iron',
    steinmetz: 'stone',
    schmied: 'tools',
    zimmermann: 'planks',
    baecker: 'bread',
    mueller: 'flour',
    haendler: 'gold',
  };
  return map[job] ?? 'food';
}

function professionModifier(job: JobType, season?: SeasonState): number {
  if (!season) return 1;
  if (season.currentSeason === 'winter' && (job === 'bauer' || job === 'fischer')) return 0.55;
  if (season.currentSeason === 'autumn' && job === 'bauer') return 1.35;
  if (season.currentSeason === 'spring' && (job === 'bauer' || job === 'holzfaeller')) return 1.15;
  return 1;
}

function softCap(level: number): number {
  return Math.min(2.4, 1 + Math.log2(Math.max(1, level)) * 0.18);
}

function calculatePhaseIndex(viewers: number, population: number, buildingCount: number, progressionLevel: number): number {
  const score = calculateProgressionScore(viewers, population, buildingCount, progressionLevel);
  if (score >= 180) return 8;
  if (score >= 145) return 7;
  if (score >= 120) return 6;
  if (score >= 100) return 5;
  if (score >= 85) return 4;
  if (score >= 75) return 3;
  if (score >= 42) return 2;
  if (score >= 10) return 1;
  return 0;
}

function calculateProgressionScore(viewers: number, population: number, buildingCount: number, progressionLevel: number): number {
  return viewers * 4 + population * 2 + buildingCount + progressionLevel * 3;
}

function calculateNpcLimit(chatterCount: number): number {
  if (chatterCount <= 10) return 20;
  if (chatterCount <= 25) return 15;
  if (chatterCount <= 50) return 10;
  return 5;
}

function chooseVoteWinner(vote: CommunityVote): string | undefined {
  const tally = new Map<string, number>();
  for (const choice of Object.values(vote.votes)) tally.set(choice, (tally.get(choice) ?? 0) + 1);
  return [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

function findWorkplaceForJob(job: JobType, buildings: readonly WorldEntity[]): WorldEntity | undefined {
  const preferred = findWorkplaceId(job);
  if (!preferred) return buildings.find((building) => building.kind === 'warehouse');
  return buildings.find((building) => building.kind === preferred) ?? buildings.find((building) => building.kind === 'warehouse');
}

function describeTask(job: JobType, daySegment: number, workplace?: string): string {
  if (daySegment === 3) return 'schläft im Wohnhaus';
  if (daySegment === 2 && (job === 'haendler' || job === 'soldat' || job === 'wache')) return 'patrouilliert am Dorfweg';
  if (daySegment === 2) return 'macht Pause am Markt';
  const place = workplace ? ` bei ${workplace}` : '';
  const tasks: Record<JobType, string> = {
    holzfaeller: `fällt Bäume${place}`,
    bauer: `arbeitet auf dem Feld${place}`,
    jaeger: 'sucht Wild am Waldrand',
    fischer: 'prüft Fischgründe',
    bergmann: `fördert Erz${place}`,
    steinmetz: `bricht Stein${place}`,
    schmied: `schmiedet Werkzeuge${place}`,
    zimmermann: `sägt Bretter${place}`,
    baecker: `backt Brot${place}`,
    mueller: `mahlt Getreide${place}`,
    haendler: `transportiert Waren${place}`,
    soldat: `trainiert${place}`,
    priester: `stärkt Moral${place}`,
    arzt: `versorgt Verletzte${place}`,
    forscher: `forscht an neuen Techniken${place}`,
    baumeister: `plant Bauprojekte${place}`,
    wache: `bewacht die Wege${place}`,
  };
  return tasks[job];
}

function cloneViewerCharacter(character: ViewerCharacter): ViewerCharacter {
  return {
    ...character,
    ownerTwitchUserId: character.ownerTwitchUserId ?? character.twitchId,
    inventory: { ...character.inventory },
    titles: [...character.titles],
    achievements: [...character.achievements],
    offlineProgress: { ...character.offlineProgress, resources: { ...character.offlineProgress.resources } },
  };
}

function cloneProject(project: CommunityProject): CommunityProject {
  return { ...project, requiredResources: { ...project.requiredResources }, contributions: { ...project.contributions } };
}

function cloneVote(vote: CommunityVote): CommunityVote {
  return { ...vote, choices: [...vote.choices], votes: { ...vote.votes } };
}

function cloneResources(resources: ExtendedResourceState): ExtendedResourceState {
  return { ...resources };
}

function jobLabel(job: JobType): string {
  const labels: Record<JobType, string> = {
    holzfaeller: 'Holzfäller',
    bauer: 'Bauer',
    jaeger: 'Jäger',
    fischer: 'Fischer',
    bergmann: 'Bergmann',
    steinmetz: 'Steinmetz',
    schmied: 'Schmied',
    zimmermann: 'Zimmermann',
    baecker: 'Bäcker',
    mueller: 'Müller',
    haendler: 'Händler',
    soldat: 'Soldat',
    priester: 'Priester',
    arzt: 'Arzt',
    forscher: 'Forscher',
    baumeister: 'Baumeister',
    wache: 'Wache',
  };
  return labels[job];
}

function resourceLabel(resource: ExtendedResourceName): string {
  const labels: Partial<Record<ExtendedResourceName, string>> = {
    wood: 'Holz',
    stone: 'Stein',
    food: 'Nahrung',
    gold: 'Gold',
    water: 'Wasser',
    tools: 'Werkzeuge',
    iron: 'Eisen',
    planks: 'Bretter',
    grain: 'Getreide',
    bread: 'Brot',
    fish: 'Fisch',
    meat: 'Fleisch',
  };
  return labels[resource] ?? resource;
}
